import { registerCallClassRules } from '@retail-ops/shared-types';
/**
 * ConfigContext — loads the v3 config bundle once at app start.
 *
 * Behaviour per teams-app-wiring §4.2:
 *   - On mount, `Promise.allSettled` fetch all 22 /v3/config/* routes.
 *   - 5-min in-memory module-level cache. Mounts within the TTL reuse the cache;
 *     `reload()` bypasses it.
 *   - Graceful 503 ("config not seeded") handling → `state: 'initializing'` so
 *     the shell renders a friendly banner instead of a fatal error.
 *   - Network / other 5xx → `state: 'error'` with a retry button surfaced by
 *     calling `reload()`.
 *
 * The bundle shape is the real `ConfigBundle` from `@retail-ops/shared-types`.
 * Every field is populated from exactly one `/v3/config/*` endpoint; routes that
 * 503 leave the corresponding field at its empty default.
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { ApiError } from '@retail-ops/api-client';
import type {
  ConfigBundle as SharedConfigBundle,
  Product,
  Feature,
  Region,
  Branch,
  DisclosureTemplate,
  NotificationTemplatesEnvelope,
} from '@retail-ops/shared-types';
import { useApiClient } from './ApiClientContext';
import { useAuth } from './AuthContext';
import { registerZipPlus4Setting } from '@/utils/zipPlus4';

/**
 * Re-exports for module agents that reference the Wave-0 type aliases.
 * The canonical types live in `@retail-ops/shared-types/config`; these are
 * the exact same shapes, re-exposed under the names downstream code imports.
 */
export type ProductDefinition = Product;
export type FeatureDefinition = Feature;
export type RegionDefinition = Region;
export type BranchDefinition = Branch;
export type DisclosureDefinition = DisclosureTemplate;

/**
 * `ConfigBundle` exposed from the Teams ConfigContext.
 *
 * Structurally the real `@retail-ops/shared-types` `ConfigBundle` + a handful
 * of legacy stub arrays that Wave-0 module scaffolds reference. The stub
 * fields are always empty in Wave 1; they stay on the surface so module
 * agents compiling in parallel don't regress. When the SQL seeds for those
 * tables land, they'll fold back into the shared-types bundle and these
 * locally-added fields can be removed.
 */
export interface ConfigBundle extends SharedConfigBundle {
  serviceTypes: ReadonlyArray<{ code: string; label: string; requiredPermission?: string }>;
  approvalRules: ReadonlyArray<{ category: string; approverRole: string }>;
  pipelineSteps: ReadonlyArray<{ stepName: string; order: number }>;
  killListReasons: ReadonlyArray<{ reason: string; defaultAction: string }>;
  auditReportTemplates: ReadonlyArray<{ code: string; name: string }>;
}

export type ConfigLoadState = 'idle' | 'loading' | 'loaded' | 'initializing' | 'error';

export interface ConfigContextValue {
  config: ConfigBundle;
  state: ConfigLoadState;
  error: string | null;
  lastLoadedAt: Date | null;
  /** Force a fresh fetch, bypassing the 5-min cache. */
  reload: () => Promise<void>;
}

const ConfigContext = createContext<ConfigContextValue | null>(null);

const CACHE_TTL_MS = 5 * 60 * 1000;

// ---------------------------------------------------------------------------
// Module-level cache — survives unmount/remount within TTL.
// Keyed by currently-active user email so a role change invalidates naturally.
// ---------------------------------------------------------------------------

interface CacheEntry {
  bundle: ConfigBundle;
  state: ConfigLoadState;
  error: string | null;
  loadedAt: Date;
  email: string;
}

let moduleCache: CacheEntry | null = null;

function isCacheFresh(entry: CacheEntry | null, email: string): entry is CacheEntry {
  if (!entry) return false;
  if (entry.email !== email) return false;
  return Date.now() - entry.loadedAt.getTime() < CACHE_TTL_MS;
}

// ---------------------------------------------------------------------------
// Empty bundle — used as the seed before anything resolves.
// ---------------------------------------------------------------------------

const EMPTY_BUNDLE: ConfigBundle = {
  systemSettings: {},
  featureFlags: [],

  products: [],
  features: [],
  productFeatures: [],
  cardTypes: [],

  disclosures: [],
  entityRules: [],
  entityRestrictions: [],

  regions: [],
  branches: [],

  naicsCodes: [],

  cdPenaltyRules: [],
  cdPenaltyWaiverReasons: [],
  coreDirectorCodes: [],
  cipIdPriority: [],
  disclosureVersions: [],
  disclosureSections: [],
  disclosureVariants: [],
  mergeFieldGroups: [],
  mergeFieldDefinitions: [],
  entityRequiredDocs: [],
  entityComplianceGates: [],
  entityRequiredRelationships: [],
  entityRestrictedServices: [],
  sdbTimeline: [],
  sdbFeeSchedule: [],
  iraConfig: [],
  alertThresholds: [],
  pipelineDefinitions: [],
  slaTargets: [],
  signatureAuthRules: [],
  retentionPolicies: [],
  notificationTemplates: {
    templates: [],
    versions: [],
    variables: [],
    routingRules: [],
    channels: [],
    categories: [],
  },

  systemSettingsRows: [],

  // Legacy Wave-0 stubs — always empty until SQL seeds land.
  serviceTypes: [],
  approvalRules: [],
  pipelineSteps: [],
  killListReasons: [],
  auditReportTemplates: [],

  loadedAt: new Date(0).toISOString(),
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type SettledEnvelope<T> = PromiseSettledResult<{
  success: boolean;
  data?: T;
  error?: string;
}>;

/**
 * Unwrap a settled api-client promise into either the typed payload, an
 * explicit "not seeded" signal (503), or a hard-error message.
 */
type SettledOutcome<T> =
  | { kind: 'ok'; value: T | undefined }
  | { kind: 'not-seeded' }
  | { kind: 'error'; message: string };

function unwrap<T>(result: SettledEnvelope<T>): SettledOutcome<T> {
  if (result.status === 'fulfilled') {
    const envelope = result.value;
    if (envelope.success && envelope.data !== undefined) {
      return { kind: 'ok', value: envelope.data };
    }
    return { kind: 'ok', value: undefined };
  }
  const reason = result.reason;
  if (ApiError.isApiError(reason)) {
    if (reason.httpStatus === 503) return { kind: 'not-seeded' };
    return { kind: 'error', message: reason.message };
  }
  const message = reason instanceof Error ? reason.message : String(reason);
  return { kind: 'error', message };
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function ConfigProvider({ children }: { children: ReactNode }): JSX.Element {
  const client = useApiClient();
  const { user } = useAuth();
  const email = user?.upn ?? '';

  // Seed from cache if possible so remount within TTL is instant.
  const initialCache = isCacheFresh(moduleCache, email) ? moduleCache : null;

  const [config, setConfig] = useState<ConfigBundle>(initialCache?.bundle ?? EMPTY_BUNDLE);
  const [state, setState] = useState<ConfigLoadState>(initialCache?.state ?? 'idle');
  const [error, setError] = useState<string | null>(initialCache?.error ?? null);
  const [lastLoadedAt, setLastLoadedAt] = useState<Date | null>(
    initialCache?.loadedAt ?? null,
  );

  // Track in-flight load so duplicate mounts don't fire parallel waves.
  const inFlight = useRef<Promise<void> | null>(null);

  const runLoad = useCallback(
    async (bypassCache: boolean): Promise<void> => {
      if (!email) {
        // Auth not yet resolved — defer.
        return;
      }

      if (!bypassCache && isCacheFresh(moduleCache, email)) {
        const c = moduleCache;
        registerCallClassRules(c.bundle.entityRules as never);
        setConfig(c.bundle);
        setState(c.state);
        setError(c.error);
        setLastLoadedAt(c.loadedAt);
        return;
      }

      setState('loading');
      setError(null);

      // 22 GET routes — fire all in parallel.
      const settled = await Promise.allSettled([
        /*  0 */ client.retail.config.systemSettings(),
        /*  1 */ client.retail.config.systemSettingsRows(),
        /*  2 */ client.retail.config.products(),
        /*  3 */ client.retail.config.features(),
        /*  4 */ client.retail.config.productFeatures(),
        /*  5 */ client.retail.config.cardTypes(),
        /*  6 */ client.retail.config.disclosures(),
        /*  7 */ client.retail.config.entityRules(),
        /*  8 */ client.retail.config.entityRestrictions(),
        /*  9 */ client.retail.config.regions(),
        /* 10 */ client.retail.config.branches(),
        /* 11 */ client.retail.config.naicsCodes(),
        /* 12 */ client.retail.config.cdPenaltyRules(),
        /* 13 */ client.retail.config.sdbTimeline(),
        /* 14 */ Promise.resolve({ success: true, data: [] }), // ComplianceQuestions retired 2026-05-08 — slot kept to preserve positional indices.
        /* 15 */ client.retail.config.iraConfig(),
        /* 16 */ client.retail.config.alertThresholds(),
        /* 17 */ client.retail.config.pipelineDefinitions(),
        /* 18 */ client.retail.config.slaTargets(),
        /* 19 */ client.retail.config.signatureAuthRules(),
        /* 20 */ client.retail.config.retentionPolicies(),
        /* 21 */ client.retail.config.notificationTemplates(),
        /* 22 */ client.retail.config.cdPenaltyWaiverReasons(),
        /* 23 */ client.retail.config.coreDirectorCodes(),
        /* 24 */ client.retail.config.cipIdPriority(),
      ]);

      const notSeededCount = settled.filter(
        (r) =>
          r.status === 'rejected' &&
          ApiError.isApiError(r.reason) &&
          r.reason.httpStatus === 503,
      ).length;

      // Non-503 errors — network, 500, etc.
      const hardFailures: string[] = [];
      for (const r of settled) {
        if (
          r.status === 'rejected' &&
          !(ApiError.isApiError(r.reason) && r.reason.httpStatus === 503)
        ) {
          const msg = ApiError.isApiError(r.reason)
            ? r.reason.message
            : r.reason instanceof Error
              ? r.reason.message
              : String(r.reason);
          hardFailures.push(msg);
        }
      }

      const pick = <T,>(idx: number, fallback: T): T => {
        const out = unwrap<T>(settled[idx] as SettledEnvelope<T>);
        if (out.kind === 'ok' && out.value !== undefined) return out.value;
        return fallback;
      };

      // v0.1.30 defect fix (smoke-test "t.filter is not a function" on
      // PersonModal open): several /v3/config/* routes return composite
      // envelopes like { questions, categories } instead of bare arrays.
      // The old `pick(idx, [])` stored the whole object into the
      // ConfigBundle field typed as ReadonlyArray<T>; the first consumer
      // to call `.filter()` on it blew up. This helper extracts the array
      // from the envelope. It tolerates both shapes (bare array or
      // composite object) so the client doesn't have to re-deploy every
      // time a route's response grows a sibling list.
      const pickArray = <T,>(idx: number, key: string): ReadonlyArray<T> => {
        const raw = pick<unknown>(idx, []);
        if (Array.isArray(raw)) return raw as ReadonlyArray<T>;
        if (raw && typeof raw === 'object' && Array.isArray((raw as Record<string, unknown>)[key])) {
          return (raw as Record<string, ReadonlyArray<T>>)[key] as ReadonlyArray<T>;
        }
        return [];
      };

      // /v3/config/system-settings returns ROWS ({ settingKey, settingValue, … }),
      // but every reader indexes systemSettings by key. Until 0.122.1 those
      // map-style reads all got undefined and silently used their defaults, so
      // admin toggles (ZIP+4, address-verification gate, …) never took effect.
      // Flatten the rows into key → value once, here. A map payload passes through.
      const toSettingsMap = (raw: unknown): Record<string, unknown> => {
        if (!Array.isArray(raw)) {
          return raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
        }
        const map: Record<string, unknown> = {};
        for (const row of raw as ReadonlyArray<Record<string, unknown>>) {
          if (!row || typeof row !== 'object') continue;
          const key = typeof row.settingKey === 'string' ? row.settingKey
            : typeof row.key === 'string' ? row.key : undefined;
          if (key) map[key] = row.settingValue ?? row.value;
        }
        return map;
      };

      const nextBundle: ConfigBundle = {
        systemSettings: toSettingsMap(pick<unknown>(0, {})),
        systemSettingsRows: pick(1, []),

        products: pick(2, []),
        features: pick(3, []),
        productFeatures: pick(4, []),
        cardTypes: pick(5, []),

        // P0 fix 2026-04-27: route 6 returns composite envelope
        // {templates, versions, sections, variants, mergeFieldGroups,
        //  mergeFieldDefinitions}. Unwrap each into its own bundle field.
        disclosures: pickArray(6, 'templates'),
        disclosureVersions: pickArray(6, 'versions'),
        disclosureSections: pickArray(6, 'sections'),
        disclosureVariants: pickArray(6, 'variants'),
        mergeFieldGroups: pickArray(6, 'mergeFieldGroups'),
        mergeFieldDefinitions: pickArray(6, 'mergeFieldDefinitions'),
        entityRules: pickArray(7, 'rules'),
        entityRequiredDocs: pickArray(7, 'requiredDocs'),
        entityComplianceGates: pickArray(7, 'complianceGates'),
        entityRequiredRelationships: pickArray(7, 'requiredRelationships'),
        entityRestrictedServices: pickArray(7, 'restrictedServices'),
        entityRestrictions: pick(8, []),

        regions: pick(9, []),
        branches: pick(10, []),

        // Routes 11 + 12 ALSO return composite envelopes ({codes,sectors} /
        // {rules,waiverReasons}) — they were missed in the original unwrap pass,
        // so the bundle stored the whole object into an array-typed field
        // (config.cdPenaltyRules.length read undefined in RCD). Unwrap the
        // primary list like the other envelope routes below. (Fixed 2026-06-23.)
        naicsCodes: pickArray(11, 'codes'),

        cdPenaltyRules: pickArray(12, 'rules'),
        cdPenaltyWaiverReasons: pick(22, []),
        coreDirectorCodes: pick(23, []),
        cipIdPriority: pick(24, []),
        // These 7 routes return composite envelopes — unwrap to the array.
        sdbTimeline: pickArray(13, 'milestones'),
        sdbFeeSchedule: [],
        iraConfig: pickArray(15, 'planPrefixes'),
        alertThresholds: pickArray(16, 'thresholds'),
        pipelineDefinitions: pickArray(17, 'definitions'),
        slaTargets: pickArray(18, 'targets'),
        signatureAuthRules: pickArray(19, 'rules'),
        retentionPolicies: pickArray(20, 'policies'),
        // Route 21 returns a composite envelope (not a bare array) — see
        // ConfigFunction.cs §21 and NotificationTemplatesEnvelope. Fallback
        // must mirror the envelope shape so downstream `.templates.filter(...)`
        // etc. never throws when the route 503s / hasn't seeded.
        notificationTemplates: pick<NotificationTemplatesEnvelope>(21, {
          templates: [],
          versions: [],
          variables: [],
          routingRules: [],
          channels: [],
          categories: [],
        }),

        featureFlags: [],

        // Legacy Wave-0 stubs — empty until SQL seeds land for these tables.
        serviceTypes: [],
        approvalRules: [],
        pipelineSteps: [],
        killListReasons: [],
        auditReportTemplates: [],

        loadedAt: new Date().toISOString(),
      };

      // Decide overall state:
      //   - Any hard failure → 'error' (retryable).
      //   - No hard failures, but 1+ routes 503 → 'initializing'.
      //   - Everything 200 → 'loaded'.
      let nextState: ConfigLoadState;
      let nextError: string | null = null;
      if (hardFailures.length > 0) {
        nextState = 'error';
        nextError = hardFailures[0] ?? 'Config load failed';
      } else if (notSeededCount > 0) {
        nextState = 'initializing';
      } else {
        nextState = 'loaded';
      }

      const now = new Date();
      moduleCache = {
        bundle: nextBundle,
        state: nextState,
        error: nextError,
        loadedAt: now,
        email,
      };

      registerZipPlus4Setting(nextBundle.systemSettings);
      // The AcctClsfCode that gates products and travels to Core now comes from
      // EntityRules rather than the hardcoded CALL_CLASS_MAP, so Compliance can
      // change a classification without a deploy. Registered here because
      // callClassFor() is called from pure predicates with no context access;
      // the map remains the fallback until this lands. (Bill 2026-09-23.)
      registerCallClassRules(nextBundle.entityRules as never);

      setConfig(nextBundle);
      setState(nextState);
      setError(nextError);
      setLastLoadedAt(now);
    },
    [client, email],
  );

  const load = useCallback(async (): Promise<void> => {
    if (inFlight.current) return inFlight.current;
    const p = runLoad(false).finally(() => {
      inFlight.current = null;
    });
    inFlight.current = p;
    return p;
  }, [runLoad]);

  const reload = useCallback(async (): Promise<void> => {
    const p = runLoad(true).finally(() => {
      inFlight.current = null;
    });
    inFlight.current = p;
    return p;
  }, [runLoad]);

  useEffect(() => {
    if (!email) return;
    void load();
  }, [email, load]);

  const value = useMemo<ConfigContextValue>(
    () => ({ config, state, error, lastLoadedAt, reload }),
    [config, state, error, lastLoadedAt, reload],
  );

  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>;
}

export function useConfig(): ConfigContextValue {
  const ctx = useContext(ConfigContext);
  if (!ctx) throw new Error('useConfig must be used within ConfigProvider');
  return ctx;
}
